import React, { useContext, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import authContext from '../../../../contexts/AuthContext';
import {
  AppBar, Avatar, Button, Chip, IconButton,
  Toolbar, Typography, Hidden,
  Menu, MenuItem, ListItemIcon, ListItemText, Divider,
} from '@material-ui/core';
import HelpOutlineIcon    from '@material-ui/icons/HelpOutline';
import ExitToAppIcon      from '@material-ui/icons/ExitToApp';
import MenuIcon           from '@material-ui/icons/Menu';
import PersonIcon         from '@material-ui/icons/Person';
import LockOutlinedIcon   from '@material-ui/icons/LockOutlined';
import HeadsetMicIcon     from '@material-ui/icons/HeadsetMic';
import ArrowDropDownIcon  from '@material-ui/icons/ArrowDropDown';

const useStyles = makeStyles(theme => ({
  root: {
    background: 'linear-gradient(90deg, #682622 0%, #4e1b18 100%)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.07), 0 2px 12px rgba(0,0,0,0.25)',
  },
  toolbar: {
    minHeight: 56,
    padding: theme.spacing(0, 2.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },

  /* ── Brand ── */
  brand: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1.25),
    textDecoration: 'none', flexShrink: 0,
  },
  logo: {
    width: 34, height: 34, borderRadius: 8,
    border: '2px solid rgba(255,255,255,0.3)', objectFit: 'cover',
  },
  brandName: {
    color: '#fff', fontWeight: 700, fontSize: '0.9rem',
    lineHeight: 1.2, letterSpacing: '0.3px',
  },
  brandTag: {
    color: 'rgba(255,255,255,0.55)', fontSize: '0.62rem',
    lineHeight: 1, letterSpacing: '0.2px',
  },

  flex: { flex: 1 },

  /* ── Right side ── */
  rightGroup: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1),
  },

  /* ── Account pill button ── */
  accountBtn: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '4px 10px 4px 6px',
    borderRadius: 24,
    border: '1.5px solid rgba(255,255,255,0.28)',
    backgroundColor: 'rgba(255,255,255,0.1)',
    cursor: 'pointer', textTransform: 'none',
    color: '#fff', minWidth: 0,
    transition: 'background-color 0.15s, border-color 0.15s',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.18)',
      borderColor: 'rgba(255,255,255,0.55)',
    },
  },
  userAvatar: {
    width: 28, height: 28,
    backgroundColor: 'rgba(255,255,255,0.25)',
    color: '#fff', fontSize: '0.78rem', fontWeight: 700,
    border: '1.5px solid rgba(255,255,255,0.4)',
    flexShrink: 0,
  },
  accountName: {
    fontSize: '0.78rem', fontWeight: 600,
    color: '#fff', lineHeight: 1,
    maxWidth: 110, overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    marginLeft: 4,
  },
  chevron: {
    fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', flexShrink: 0,
  },

  divider: {
    width: 1, height: 22,
    backgroundColor: 'rgba(255,255,255,0.2)', flexShrink: 0,
    margin: theme.spacing(0, 0.5),
  },
  helpBtn: {
    color: 'rgba(255,255,255,0.65)', padding: 6,
    '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
  },
  signOutBtn: {
    color: 'rgba(255,255,255,0.85)', textTransform: 'none',
    fontSize: '0.8rem', fontWeight: 500, borderRadius: 8,
    padding: theme.spacing(0.5, 1.5),
    border: '1px solid rgba(255,255,255,0.22)',
    gap: 6, minWidth: 0,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
      borderColor: 'rgba(255,255,255,0.4)', color: '#fff',
    },
  },
  mobileMenuBtn: { color: '#fff', padding: 6 },

  /* ── Profile dropdown ── */
  menuPaper: {
    borderRadius: '10px !important',
    boxShadow: '0 4px 20px rgba(0,0,0,0.14) !important',
    minWidth: 248,
    marginTop: 6,
  },
  menuHeader: {
    borderBottom: '1px solid #f0f0f0',
    pointerEvents: 'none',
    overflow: 'hidden',
  },
  menuHeaderBand: {
    height: 4,
    background: 'linear-gradient(90deg, #682622 0%, #9b3a35 100%)',
  },
  menuHeaderBody: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.75, 2, 1, 2),
  },
  menuHeaderAvatar: {
    width: 42, height: 42,
    backgroundColor: '#682622',
    color: '#fff',
    fontSize: '1rem', fontWeight: 700,
    flexShrink: 0,
    border: '2px solid #e8d5d4',
  },
  menuHeaderInfo: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
  },
  menuHeaderFullName: {
    fontWeight: 700, fontSize: '0.9rem', color: '#1a1a1a', lineHeight: 1.3,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  menuHeaderUsername: { fontSize: '0.72rem', color: '#90a4ae', marginTop: 1 },
  menuHeaderFooter: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1),
    padding: theme.spacing(0.75, 2, 1.25, 2),
  },
  menuHeaderRole: {
    height: 18, fontSize: '0.65rem', fontWeight: 600,
    backgroundColor: '#68262214', color: '#682622',
    borderRadius: 9, flexShrink: 0,
  },
  menuHeaderEmail: { fontSize: '0.7rem', color: '#90a4ae' },
  menuItem: {
    padding: theme.spacing(1, 2),
    gap: 10,
    fontSize: '0.82rem',
    '& .MuiListItemIcon-root': { minWidth: 28, color: '#546e7a' },
  },
  menuItemDanger: {
    padding: theme.spacing(1, 2),
    gap: 10, fontSize: '0.82rem',
    color: '#c62828',
    '& .MuiListItemIcon-root': { minWidth: 28, color: '#c62828' },
  },
}));

const TopBar = ({ onOpenNavBarMobile, className, ...rest }) => {
  const [{ username, name, email, role }, dispatch] = useContext(authContext);
  const classes  = useStyles();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu  = e => setAnchorEl(e.currentTarget);
  const closeMenu = ()  => setAnchorEl(null);

  const handleLogout = () => {
    closeMenu();
    dispatch({ type: 'LOGOUT' });
    navigate('/auth/login');
  };

  const handleNavigate = path => { closeMenu(); navigate(path); };

  const initial = username ? username.charAt(0).toUpperCase() : '?';

  return (
    <AppBar {...rest} className={`${classes.root} ${className || ''}`} position="fixed">
      <Toolbar className={classes.toolbar} disableGutters>

        {/* ── Brand ── */}
        <RouterLink className={classes.brand} to="/overview">
          <img alt="TDGG" className={classes.logo} src="/images/adgg_avatar.png" />
          <div>
            <Typography className={classes.brandName}>TDGG WEB</Typography>
            <Typography className={classes.brandTag}>More Productive &amp; Profitable Dairy Cows</Typography>
          </div>
        </RouterLink>

        <div className={classes.flex} />

        {/* ── Desktop right ── */}
        <Hidden mdDown implementation="css">
          <div className={classes.rightGroup}>

            <div className={classes.divider} />

            <RouterLink to="/user-guide/USER_MANUAL.chm" target="_blank" download style={{ display: 'flex' }}>
              <IconButton className={classes.helpBtn} size="small" title="Help / User Guide">
                <HelpOutlineIcon style={{ fontSize: '1.15rem' }} />
              </IconButton>
            </RouterLink>

            {/* ── Account pill → profile dropdown ── */}
            <Button className={classes.accountBtn} onClick={openMenu} disableRipple={false}>
              <Avatar className={classes.userAvatar}>{initial}</Avatar>
              <Typography className={classes.accountName}>{username}</Typography>
              <ArrowDropDownIcon className={classes.chevron} />
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={closeMenu}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top',    horizontal: 'right' }}
              PaperProps={{ className: classes.menuPaper }}
            >
              {/* User header */}
              <div className={classes.menuHeader}>
                <div className={classes.menuHeaderBand} />
                <div className={classes.menuHeaderBody}>
                  <Avatar className={classes.menuHeaderAvatar}>{initial}</Avatar>
                  <div className={classes.menuHeaderInfo}>
                    {name && <Typography className={classes.menuHeaderFullName}>{name}</Typography>}
                    <Typography className={classes.menuHeaderUsername}>@{username}</Typography>
                  </div>
                </div>
                <div className={classes.menuHeaderFooter}>
                  {role && <Chip label={role} size="small" className={classes.menuHeaderRole} />}
                  {email && <Typography className={classes.menuHeaderEmail}>{email}</Typography>}
                </div>
              </div>

              <MenuItem className={classes.menuItem} onClick={() => handleNavigate('/settings/general')}>
                <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="My Profile" primaryTypographyProps={{ style: { fontSize: '0.82rem' } }} />
              </MenuItem>

              <MenuItem className={classes.menuItem} onClick={() => handleNavigate('/settings/security')}>
                <ListItemIcon><LockOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Change Password" primaryTypographyProps={{ style: { fontSize: '0.82rem' } }} />
              </MenuItem>

              <Divider style={{ margin: '4px 0' }} />

              <a href="mailto:aadgg-support@cgiar.org" style={{ textDecoration: 'none' }}>
                <MenuItem className={classes.menuItem}>
                  <ListItemIcon><HeadsetMicIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary="Contact Support"
                    secondary="aadgg-support@cgiar.org"
                    primaryTypographyProps={{ style: { fontSize: '0.82rem' } }}
                    secondaryTypographyProps={{ style: { fontSize: '0.7rem' } }}
                  />
                </MenuItem>
              </a>

              <Divider style={{ margin: '4px 0' }} />

              <MenuItem className={classes.menuItemDanger} onClick={handleLogout}>
                <ListItemIcon><ExitToAppIcon fontSize="small" style={{ color: '#c62828' }} /></ListItemIcon>
                <ListItemText primary="Sign Out" primaryTypographyProps={{ style: { fontSize: '0.82rem', color: '#c62828' } }} />
              </MenuItem>
            </Menu>

          </div>
        </Hidden>

        {/* ── Mobile hamburger ── */}
        <Hidden lgUp implementation="css">
          <IconButton className={classes.mobileMenuBtn} onClick={onOpenNavBarMobile}>
            <MenuIcon />
          </IconButton>
        </Hidden>

      </Toolbar>
    </AppBar>
  );
};

TopBar.propTypes = {
  className: PropTypes.string,
  onOpenNavBarMobile: PropTypes.func,
};

export default TopBar;
